import axiosInstance from './axiosInstance';

/**
 * 获取所有视频文件
 * @param {Object} filters - 筛选条件
 * @param {Object} pagination - 分页参数 {page, page_size}
 * @returns {Promise<Object>} 包含视频列表和总数的对象 {items: Array, total: number}
 */
export const getVideoFiles = async (filters = {}, pagination = {page: 1, page_size: 10}) => {
  try {
    const response = await axiosInstance.get('/live-sim/videos', { 
      params: {
        ...filters,
        page: pagination.page,
        page_size: pagination.page_size
      }
    });
    
    // 提取数据部分
    let data = response.data;
    
    // 判断返回格式
    if (data && data.code === 0 && data.data) {
      // 检查是否是分页格式
      if (data.data.items && data.data.total !== undefined) {
        return {
          items: data.data.items,
          total: data.data.total,
          page: data.data.page || pagination.page,
          page_size: data.data.page_size || pagination.page_size
        };
      } else if (Array.isArray(data.data)) {
        // 旧格式，仅返回数组
        return {
          items: data.data,
          total: data.data.length
        };
      }
      
      return {
        items: Array.isArray(data.data) ? data.data : [],
        total: Array.isArray(data.data) ? data.data.length : 0
      };
    } else if (Array.isArray(data)) {
      // 直接返回数组
      return {
        items: data,
        total: data.length
      };
    } else if (data && data.items && data.total !== undefined) {
      // 直接返回分页对象
      return data;
    } else {
      // 其他格式，默认返回空数组
      return {
        items: [],
        total: 0
      };
    }
  } catch (error) {
    console.error('获取视频文件列表失败:', error);
    console.error('错误详情:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * 获取所有直播流记录（包括活跃和已结束的）
 * @param {Object} params - 分页参数 {page: 1, page_size: 10}
 * @returns {Promise<Object>} 包含直播流列表的响应对象 {code: 0, message: 'success', data: {items: Array, total: number}}
 */
export const getActiveStreams = async (params = {}) => {
  try {
    // 设置默认分页参数
    const { page = 1, page_size = 10 } = params;
    
    const response = await axiosInstance.get('/live-sim/streams', {
      params: {
        page,
        page_size
      }
    });
    
    // 提取数据部分
    const rawData = response.data;
    
    // 标准化响应格式
    let standardResponse = {
      code: 0,
      message: 'success',
      data: {
        items: [],
        total: 0
      }
    };
    
    // 处理后端分页响应
    if (rawData) {
      // 检查是否是标准API响应格式 {code: 0, message: 'success', data: {...}}
      if (rawData.code !== undefined) {
        standardResponse.code = rawData.code;
        standardResponse.message = rawData.message || 'success';
        
        if (rawData.code === 0) {
          // 检查是否是分页格式
          if (rawData.data && rawData.data.items && Array.isArray(rawData.data.items)) {
            // 标准分页格式: {code: 0, data: {items: [], total: 0}}
            standardResponse.data = {
              items: rawData.data.items,
              total: rawData.data.total || 0
            };
          } else if (Array.isArray(rawData.data)) {
            // 兼容旧格式：{code: 0, data: []}
            standardResponse.data = {
              items: rawData.data,
              total: rawData.data.length
            };
          } else {
            console.warn('无法识别的data格式:', rawData.data);
            standardResponse.data = {
              items: [],
              total: 0
            };
          }
        } else {
          // API返回错误码
          console.error('API返回错误码:', rawData.code, rawData.message);
        }
      } 
      // 检查是否是直接的分页数据格式 {items: [], total: 0, page: 1, page_size: 10}
      else if (rawData.items && Array.isArray(rawData.items) && rawData.total !== undefined) {
        // 直接分页格式（可能是后端没有正确包装）
        standardResponse.code = 0;
        standardResponse.message = 'success';
        standardResponse.data = {
          items: rawData.items,
          total: rawData.total
        };
      }
      // 检查是否是直接的数组格式
      else if (Array.isArray(rawData)) {
        // 直接数组格式
        standardResponse.data = {
          items: rawData,
          total: rawData.length
        };
      } else {
        // 其他格式
        console.warn('无法识别的响应格式:', rawData);
        standardResponse.code = -1;
        standardResponse.message = '响应格式错误';
        standardResponse.data = {
          items: [],
          total: 0
        };
      }
    } else {
      console.error('响应数据为空');
      standardResponse.code = -1;
      standardResponse.message = '响应数据为空';
    }

    return standardResponse;
  } catch (error) {
    console.error('获取活跃直播流列表失败:', error);
    console.error('错误详情:', error.response?.data || error.message);
    console.error('错误状态码:', error.response?.status);
    return {
      code: -1,
      message: error.message || '获取失败',
      data: {
        items: [],
        total: 0
      }
    };
  }
};

/**
 * 创建定时推流任务
 * @param {Object} data - 推流参数
 * @param {string} data.videoFile - 视频ID
 * @param {string} data.title - 课堂名称
 * @param {string} data.teacher_real_name - 老师称呼
 * @param {string} [data.teacher_desc] - 老师简介（可选）
 * @param {string} [data.description] - 课堂简介（可选）
 * @param {string} [data.speaker_id] - 老师ID（可选）
 * @param {Array} [data.group_ids] - 班级ID数组（可选）
 * @param {Array} [data.groupName] - 班级名称数组（可选）
 * @param {Date} data.startTime - 推流开始时间（必填）
 * @param {Date} [data.endTime] - 推流结束时间（可选）
 * @param {number} [data.duration] - 课程持续时间（秒）（可选）
 * @returns {Promise<Object>} 定时推流任务信息
 */
export const startStream = async (data) => {
  try {
    const response = await axiosInstance.post('/live-sim/streams', data);
    return response.data;
  } catch (error) {
    console.error('开始推流失败:', error);
    throw error;
  }
};

/**
 * 停止推流
 * @param {string} videoId - 视频ID
 * @returns {Promise<Object>} 操作结果
 */
export const stopStream = async (videoId) => {
  try {
    const response = await axiosInstance.delete(`/live-sim/streams/${videoId}`);
    
    // 处理不同的响应格式
    if (response && response.data) {
      // 如果是标准API响应格式
      if (response.data.code !== undefined) {
        if (response.data.code === 0) {
          return response.data;
        } else {
          throw new Error(response.data.message || '请求失败');
        }
      }
      // 直接返回响应数据
      return response.data;
    }
    
    return response;
  } catch (error) {
    console.error('停止推流失败:', error);
    
    // 确保错误有一个合理的消息
    const errorMsg = error.response?.data?.message || 
                     error.response?.data?.msg || 
                     error.message || 
                     '请求失败';
    
    // 重新包装错误，确保它有一个清晰的消息
    const enhancedError = new Error(errorMsg);
    enhancedError.originalError = error;
    enhancedError.response = error.response;
    
    throw enhancedError;
  }
};

/**
 * 清理临时文件
 * @returns {Promise<Object>} 操作结果
 */
export const cleanupTempFiles = async () => {
  try {
    const response = await axiosInstance.post('/live-sim/streams/cleanup', {}, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 60000 // 增加超时时间到60秒
    });
    
    if (response && response.data) {
      if (response.data.code !== undefined) {
        if (response.data.code === 0) {
          return response.data;
        } else {
          throw new Error(response.data.message || '清理临时文件失败');
        }
      }
      return response.data;
    }
    
    return response;
  } catch (error) {
    console.error('清理临时文件失败:', error);
    console.error('错误详情:', error.response?.data || '无详细信息');
    console.error('错误状态码:', error.response?.status || '无状态码');
    console.error('错误响应头:', error.response?.headers || '无响应头信息');
    console.error('请求配置:', error.config || '无请求配置信息');
    
    const errorMsg = error.response?.data?.message || 
                     error.response?.data?.msg || 
                     error.message || 
                     '清理临时文件请求失败';
    
    const enhancedError = new Error(errorMsg);
    enhancedError.originalError = error;
    enhancedError.response = error.response;
    
    throw enhancedError;
  }
};

/**
 * 上传视频文件
 * @param {File} file - 视频文件
 * @returns {Promise<Object>} 上传结果
 */
export const uploadVideo = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post('/live-sim/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('上传视频失败:', error);
    throw error;
  }
};

/**
 * 删除视频关联（不会真正删除网课存档中的视频）
 * @param {string} videoId - 视频ID
 * @returns {Promise<Object>} 操作结果
 */
export const deleteVideo = async (videoId) => {
  try {
    const response = await axiosInstance.delete(`/live-sim/videos/${videoId}`);
    return response.data;
  } catch (error) {
    console.error('操作失败:', error);
    throw error;
  }
};

// 开启直播外链
export const enableExternalLink = (recordId) => {
  return axiosInstance.post(`/live-sim/streams/${recordId}/enable-external-link`);
};

// 关闭直播外链
export const closeExternalLink = (recordId) => {
  return axiosInstance.post(`/live-sim/streams/${recordId}/close-external-link`);
};

/**
 * 获取第三方接口的潜在学生并保存到数据库
 * @param {number} streamId - 直播任务ID
 * @param {string} meetingId - 课堂ID
 * @param {number} [mediaType=6] - 媒体类型
 * @param {number} [pageSize=20] - 每页条数
 * @param {number} [pageStart=1] - 起始页码
 * @returns {Promise<Object>} 获取结果
 */
export const fetchVisitorInfo = async (streamId, meetingId, mediaType = 6, pageSize = 20, pageStart = 1) => {
  console.log('=== fetchVisitorInfo 调试日志 ===');
  console.log('输入参数:', { streamId, meetingId, mediaType, pageSize, pageStart });
  
  const requestBody = {
    meetingId,
    mediaType,
    pageSize,
    pageStart
  };
  
  console.log('构建的请求体:', requestBody);
  console.log('请求URL:', `/live-sim/streams/${streamId}/visitor-info/fetch`);
  
  try {
    console.log('开始发送axios请求...');
    const response = await axiosInstance.post(`/live-sim/streams/${streamId}/visitor-info/fetch`, requestBody);
    console.log('axios响应成功:', response);
    console.log('响应数据:', response.data);
    return response.data;
  } catch (error) {
    console.error('=== fetchVisitorInfo 错误日志 ===');
    console.error('错误对象:', error);
    console.error('错误消息:', error.message);
    console.error('错误响应:', error.response);
    console.error('错误响应数据:', error.response?.data);
    console.error('错误响应状态:', error.response?.status);
    console.error('错误响应头:', error.response?.headers);
    console.error('请求配置:', error.config);
    console.error('获取潜在学生失败:', error);
    throw error;
  }
};

/**
 * 获取指定直播任务的潜在学生
 * @param {number} streamId - 直播任务ID
 * @returns {Promise<Object>} 潜在学生列表
 */
export const getVisitorInfo = async (streamId) => {
  console.log('=== getVisitorInfo 调试日志 ===');
  console.log('输入参数 streamId:', streamId);
  console.log('streamId 类型:', typeof streamId);
  
  const url = `/live-sim/streams/${streamId}/visitor-info`;
  console.log('请求URL:', url);
  
  try {
    console.log('开始发送axios GET请求...');
    const response = await axiosInstance.get(url);
    console.log('axios响应成功:', response);
    console.log('响应状态:', response.status);
    console.log('响应数据:', response.data);
    console.log('响应数据类型:', typeof response.data);
    
    if (response.data) {
      console.log('返回响应数据:', response.data);
      return response.data;
    } else {
      console.warn('响应数据为空');
      return { code: 0, message: 'success', data: [] };
    }
  } catch (error) {
    console.error('=== getVisitorInfo 错误日志 ===');
    console.error('错误对象:', error);
    console.error('错误类型:', typeof error);
    console.error('错误消息:', error?.message);
    console.error('错误堆栈:', error?.stack);
    console.error('错误响应:', error?.response);
    console.error('错误响应状态:', error?.response?.status);
    console.error('错误响应数据:', error?.response?.data);
    console.error('错误响应头:', error?.response?.headers);
    console.error('请求配置:', error?.config);
    console.error('获取潜在学生失败:', error);
    throw error;
  }
}; 